import { useState } from "react"
import { Helmet } from 'react-helmet-async'
import { useTranslation } from "react-i18next"
import { useFilteredPatents } from 'src/filter/FilteredPatents'
import { ArrowPathIcon, CheckCircleIcon, CloudArrowUpIcon, EllipsisHorizontalCircleIcon, ExclamationTriangleIcon, StopCircleIcon } from "@heroicons/react/24/outline";
import { IpMetaContextProvider } from "./IpMeta"
import { toIpRight, useIpContext, useIpOwners, useIpRightImport, useIpRights } from './patent_renewals'
import _ from "lodash"
import { memberUrl } from "src/patents/utils"
import { Link } from "src/navigation"
import { importStatusExtraction } from "./modern_states"
import { importStatusToString, IpRightImportStatusInfo } from "../state/modern_states"
import { SortButton } from "src/patents/datawizard/DataWizard"
import { emptyStringAsUndefined } from "src/utils/strings";
import Modal from "src/components/Modal"
import { CostCenterSelect } from "./CostCenterSelect"
import { Member } from "src/patents/patents";
import { Field, Form, Formik } from "formik";
import { DatePicker } from "src/components/input/DatePicker";
import { useLocalState } from "src/settings/localStorage";

type RowKey = 'internalReference' | 'status' | 'owner' | 'costCenter'

const headers: { key: RowKey; label: string }[] = [
  { key: 'internalReference', label: 'Family member reference' },
  { key: 'status', label: 'IP Right status' },
  { key: 'owner', label: 'Owner' },
  { key: 'costCenter', label: 'Cost Center' },
]

export function BulkImportPage() {
  return (
    <IpMetaContextProvider>
      <BulkImportTable />
    </IpMetaContextProvider>
  )
}

type Row = {
  id: number
  member: Member
  internalReference: string
  status: IpRightImportStatusInfo
  selectable: boolean
  owner?: string,
  costCenter?: string,
}

function BulkImportTable() {
  const { t } = useTranslation()

  const {members} = useFilteredPatents()
  const {ownerById} = useIpOwners()
  const {ownerByCostCenterId, costCenterById} = useIpContext()
  const {ipRightByRef, isLoading} = useIpRights()
  const [selected, setSelected] = useState<Record<number, boolean>>({})
  const [sortField, setSortField] = useLocalState('pr-bulk-import-field', 'internalReference')
  const [sortOrder, setSortOrder] = useLocalState('pr-bulk-import-order', 1)
  const [showImportModal, setShowImportModal] = useState(false)

  // console.log({ipRightByRef})
  const rows: Row[] = members.map(member => {
    const prIpRight = ipRightByRef[member.internalReference]
    const ipRight = prIpRight ? toIpRight(prIpRight, ownerByCostCenterId) : undefined
    const ipRightImportStatus = importStatusExtraction({ member, ipRight, prIpRight, validationErrors: [] })
    return {
      id: member.familyMemberId,
      member,
      internalReference: member.internalReference,
      status: ipRightImportStatus,
      selectable: ipRightImportStatus.substatus === 'can-import',
      owner: ownerById[ownerByCostCenterId[prIpRight?.cost_center_id]]?.name,
      // owner: ownerByCostCenterId[ipRight?.cost_center_id],
      costCenter: costCenterById[prIpRight?.cost_center_id]?.name,
    }
  })

  const _selected = rows.filter(r => selected[r.id]).map(r => r.member)

  const sortFunction = (row: Row) => {
    switch (sortField) {
      case 'internalReference':
        return row.internalReference?.toLowerCase?.() ?? ''
      case 'status':
        return row.status?.substatus?.toLowerCase?.() ?? ''
      default:
        return row.internalReference?.toLowerCase?.() ?? ''
    }
  }

  const sortedRows = _(rows)
    .sortBy(sortFunction)
    .thru(sorted => sortOrder === 1 ? sorted : sorted.reverse())
    .value()

  const allSelected = rows.every(r => selected[r.id] ?? false)
  const nonSelected = _.values(selected).every(s => !s)

  function toggleAll() {
    const shouldSelect = !allSelected
    const s = _(rows).filter(r => r.selectable).map(r => [r.id, shouldSelect]).fromPairs().value()
    setSelected(s)
  }

  function toggleRow(id: number) {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <>
      {/* @ts-ignore */}
      <Helmet>
        <title>
          {t('bulk-import')} | {t('renewals')} | Patent Cockpit
        </title>
      </Helmet>
      <div className="header-row pb-1 flex flex-row gap-1 items-center max-w-4xl pr-2">
        <h2 className="modern-h2 mr-auto">{t('bulk-import')}</h2>
      </div>
      <div className="px-2 pb-6 max-w-max">
        <div className="flex justify-end pb-2">
          {isLoading && <div>Loading....</div>}
          <button className="btn-primary disabled:btn-disabled" disabled={nonSelected} type="button" onClick={() => setShowImportModal(true)}>
            {t('import')}
          </button>
        </div>
        {showImportModal && <ImportModal {...{setShowImportModal, selected: _selected }} />}
        <table>
          <thead>
            <tr className='pc-header-row'>
              <td className='pc-header-cell'>
                <label className="flex items-end gap-2 px-2">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                  <span className="text-xs uppercase text-slate-500">{t('all')}</span>
                </label>
              </td>
              {headers.map((h, hi) => (
                <td key={hi} className='pc-header-cell'>
                  <div className='px-2 flex items-center gap-1'>
                    <span>{t(h.key)}</span>
                    <SortButton
                      searchField={h.key}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      setSortField={setSortField}
                      setSortOrder={setSortOrder}
                    />
                  </div>
                </td>
              ))}
            </tr>
          </thead>
          <tbody className="text-sm">
            {sortedRows.map((row) => (
              <tr key={row.id}>
                <td className='pc-cell py-0.5'>
                  {row.selectable && <div className='px-2'>
                    <input
                      title="selected"
                      type="checkbox"
                      className="form-checkbox size-4"
                      checked={row.selectable && (selected[row.id] ?? false)}
                      disabled={!row.selectable}
                      onChange={() => toggleRow(row.id)}
                    />
                  </div>}
                </td>
                {headers.map((h) => (
                  <td key={`${row.id}-${h.key}`} className='pc-cell py-0.5'>
                    <div className='px-2'>{
                      h.key === 'internalReference' 
                      ? <CellReference {...row} />
                      : h.key === 'status'
                      ? <CellState status={row[h.key] as IpRightImportStatusInfo} />
                      : row[h.key]
                    }</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function CellReference({internalReference}) {
  return <Link to={memberUrl({internalReference})} className="underline-link">{internalReference}</Link>
}

const logoColor = {
  'missing-data': 'text-warn-600',
  'has-errors': 'text-warn-600',
  'ignored': 'text-slate-400',
  'stopped': 'text-slate-400',
  'inactive': 'text-slate-400',
  'can-import': 'text-sky-500',
  'back-office-processing': 'text-sky-500',
  'paid-for-life': 'text-green-600',
  'fully-ready': 'text-green-600',
}

const logo = {
  'missing-data': ExclamationTriangleIcon,
  'has-errors': ExclamationTriangleIcon,
  'ignored': EllipsisHorizontalCircleIcon,
  'stopped': StopCircleIcon,
  'inactive': StopCircleIcon,
  'can-import': CloudArrowUpIcon,
  'back-office-processing': ArrowPathIcon,
  'paid-for-life': CheckCircleIcon,
  'fully-ready': CheckCircleIcon,
}

function CellState({status}: {status: IpRightImportStatusInfo}) {
  const {t} = useTranslation()
  const StatusIcon = logo[status.substatus]
  const title = emptyStringAsUndefined(status.messages?.map(t)?.join(", "))
  // console.log({title, msg: status.messages})
  return (
    <div title={title} className={`flex items-center gap-2 ${logoColor[status.substatus] ?? 'text-slate-400'}`}>
      <span className="inline-flex size-4">
        {StatusIcon ? <StatusIcon aria-hidden="true" /> : null}
      </span>
      <span>
        {importStatusToString(status, t)}
      </span>
      <span>{status.status} {status.substatus}</span>
    </div>
  )
}

function ImportModal({setShowImportModal, selected}: {setShowImportModal, selected: Member[]}) {
  const {t} = useTranslation()

  const {postIpRights, deriveInitial} = useIpRightImport()
  const {owners, costCenters} = useIpContext()
  const [costCenterId, setCostCenterId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initialValues = {
    customer_estimated_next_renewal_date: null,
    cost_center_id: null,
  }

  async function onSubmit(values) {
    console.log({values})
    setIsSubmitting(true)
    const ipRights = selected.map(s => deriveInitial(s)).map(ipr => ({...ipr, ...values, annuity_year: 1}))
    await postIpRights(ipRights)
    setIsSubmitting(false)
    setShowImportModal(false)
  }

  function validate(values) {
    const error = {}
    console.log({values})
    if (!(typeof values.cost_center_id === 'string')) {
      error['cost_center_id'] = 'required'
    }
    return error
  }

  return (
    <Modal overflowHidden={false} blurClick={() => setShowImportModal(false)}>
      <Formik {...{ initialValues, onSubmit, validate }}>
        {({ errors }) => {
          const reqcc = errors['cost_center_id'] as string
          return (
            <Form>
              <div className="p-4 space-y-2">
                <h2>{t('import')}</h2>

                <div className="h-2" />

                <label htmlFor="customer_estimated_next_renewal_date" className="label">
                  {t('first-due-date-with-payment-provider')}
                </label>
                <Field name="customer_estimated_next_renewal_date" as={DatePicker} />

                <div className="h-2" />

                <label htmlFor="cost_center_id" className="label">{t('cost-center')} {reqcc && <span className="text-warn-700">*{t(reqcc)}</span>}</label>
                <Field name="cost_center_id" as={CostCenterSelect} className="min-w-lg" required {...{ owners, costCenters }} />
              </div>
              <div className="p-4 flex flex-row-reverse gap-4 bg-pcx-200 rounded-b-lg w-full">
                <button className="btn-primary whitespace-nowrap" type="submit" disabled={isSubmitting} >
                  {t('import')} {isSubmitting && "...."}
                </button>
                <button className="btn-secondary" type="button" onClick={() => setShowImportModal(false)}>
                  {t('cancel')}
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
